import FleetHelpers, {FLEET_BASE, extractFleetError, fleetFieldErrors} from './FleetHelpers';

/**
 * Route catalog + map builder (T016, US1/FR-003..007).
 * GET/POST /api/v1/fleet/routes, GET/PUT /routes/{id}, PUT /routes/{id}/status,
 * GET /stop-points?q=&province_id=&type= (merged branches + DCs w/ lat/lng).
 */
export default {
    mixins: [FleetHelpers],
    data: () => ({
        routes: [],
        isLoading: true,
        btnLoading: false,
        count: 0,

        // Filters
        searchItem: '',
        selectedActive: '',

        // Pagination
        offset: 10,
        pagination: {},
        per_page: 15,

        server_errors: {},

        // Builder state
        routeDetail: null,
        routeValue: {
            name: '',
            code: '',
            distance_km: null,
            standard_trip_price: null,
            standard_fuel_liters: null,
            standard_fuel_amount: null,
            trips_per_week: null,
            running_route: '',
            // ສ້າງຖ້ຽວອັດຕະໂນມັດທຸກມື້ — tomorrow's rounds copied at 18:00.
            auto_daily_rounds: false,
        },
        startStop: null,      // {type, id, name, lat, lng}
        routeStops: [],       // ordered [{type, id, name, lat, lng, stop_role, planned_offset_min}]

        // Stop-point search
        stopSearchTerm: '',
        stopSearchType: '',
        stopSearchProvince: '',
        stopSearchResults: [],
        stopSearchLoading: false,
        provinces: [],
    }),
    methods: {
        FetchRoutes() {
            this.isLoading = true;
            this.$http.get(`${FLEET_BASE}/routes`, {
                params: {
                    q: this.searchItem || undefined,
                    is_active: this.selectedActive === '' ? undefined : this.selectedActive,
                    page: this.pagination.current_page || 1,
                    per_page: this.per_page,
                },
            }).then((res) => {
                this.routes = res.data.data || [];
                this.pagination = res.data.meta || {};
                this.count = this.pagination.total || this.routes.length;
                this.isLoading = false;
            }).catch(() => {
                this.isLoading = false;
            });
        },
        FilterRoutes() {
            this.pagination.current_page = 1;
            this.FetchRoutes();
        },
        FetchRouteDetail(routeId) {
            return this.$http.get(`${FLEET_BASE}/routes/${routeId}`).then((res) => {
                this.routeDetail = res.data.data;
                return this.routeDetail;
            });
        },
        FetchProvinces() {
            this.$http.get('v1/listing/provinces').then((res) => {
                const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
                this.provinces = list;
            }).catch(() => {
            });
        },
        SearchStopPoints() {
            this.stopSearchLoading = true;
            this.$http.get(`${FLEET_BASE}/stop-points`, {
                params: {
                    q: this.stopSearchTerm || undefined,
                    type: this.stopSearchType || undefined,
                    province_id: this.stopSearchProvince || undefined,
                    limit: 30,
                },
            }).then((res) => {
                this.stopSearchResults = res.data.data || [];
                this.stopSearchLoading = false;
            }).catch(() => {
                this.stopSearchLoading = false;
            });
        },
        BuildRoutePayload() {
            const payload = {
                name: this.routeValue.name,
                code: this.routeValue.code || null,
                start_stop: this.startStop
                    ? {type: this.startStop.type, id: this.startStop.id}
                    : null,
                distance_km: this.routeValue.distance_km || null,
                standard_fuel_liters: this.routeValue.standard_fuel_liters || null,
                trips_per_week: this.routeValue.trips_per_week || null,
                running_route: this.routeValue.running_route || null,
                auto_daily_rounds: !!this.routeValue.auto_daily_rounds,
                stops: this.routeStops.map((stop) => ({
                    type: stop.type,
                    id: stop.id,
                    stop_role: stop.stop_role || null,
                    planned_offset_min: stop.planned_offset_min === '' || stop.planned_offset_min === null
                        ? null : Number(stop.planned_offset_min),
                })),
            };

            // The money keys are OMITTED, not sent as null, for a planner
            // without the fleet-cost permission: the update applies whichever
            // keys are PRESENT, so sending `standard_trip_price: null` from a
            // form that never loaded it would erase what finance entered.
            // (FleetRouteService strips them server-side too — this just keeps
            // the request honest about what the user actually edited.)
            if (this.CanViewCost) {
                payload.standard_trip_price = this.routeValue.standard_trip_price || null;
                payload.standard_fuel_amount = this.routeValue.standard_fuel_amount || null;
            }

            return payload;
        },
        CreateRoute() {
            this.btnLoading = true;
            this.server_errors = {};
            return this.$http.post(`${FLEET_BASE}/routes`, this.BuildRoutePayload()).then((res) => {
                this.btnLoading = false;
                this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.server_errors = fleetFieldErrors(error);
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        UpdateRoute(routeId) {
            this.btnLoading = true;
            this.server_errors = {};
            return this.$http.put(`${FLEET_BASE}/routes/${routeId}`, this.BuildRoutePayload()).then((res) => {
                this.btnLoading = false;
                this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.server_errors = fleetFieldErrors(error);
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        // Persist a map-picked pin onto the facility row itself (DCs ship with
        // lat/lng = 0 — this is how they get real coordinates).
        SaveStopPointLocation(type, id, lat, lng) {
            return this.$http.put(`${FLEET_BASE}/stop-points/${type}/${id}/location`, {lat, lng}).then((res) => {
                this.$notification.success({message: this.$t('fleetRoutes.location_saved')});
                return res.data.data;
            }).catch((error) => {
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        // The route's FIXED QR (FR-{id}) as inline SVG for the printable label.
        FetchRouteQr(routeId) {
            return this.$http.get(`${FLEET_BASE}/routes/${routeId}/qr`).then((res) => res.data.data)
                .catch((error) => {
                    this.$notification.error({message: extractFleetError(error)});
                    return Promise.reject(error);
                });
        },
        // Only the switch is sent — the update applies PRESENT keys, stops untouched.
        SetRouteAutoDaily(routeId, enabled) {
            return this.$http.put(`${FLEET_BASE}/routes/${routeId}`, {auto_daily_rounds: !!enabled}).then((res) => {
                this.$notification.success({
                    message: enabled ? this.$t('fleetRoutes.auto_daily_enabled') : this.$t('fleetRoutes.auto_daily_disabled'),
                });
                return res.data.data;
            }).catch((error) => {
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        // Draft <-> active toggle; surfaces ROUTE_HAS_NO_STOPS / ROUTE_HAS_NO_START.
        SetRouteStatus(routeId, isActive) {
            return this.$http.put(`${FLEET_BASE}/routes/${routeId}/status`, {is_active: isActive}).then((res) => {
                this.$notification.success({
                    message: isActive ? this.$t('fleetRoutes.route_activated') : this.$t('fleetRoutes.route_set_draft'),
                });
                return res.data.data;
            }).catch((error) => {
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
    },
};
